import { useState, useEffect } from 'react';
import { Search, Users, Sparkles, FileText, X, ExternalLink, GraduationCap } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { getCommittees } from '@/lib/api';
import { useLanguage } from '@/context/LanguageContext';

// Committee poster mapping
const committeePosters = {
    'sochum': 'https://customer-assets.emergentagent.com/job_28206353-b4b4-425f-a884-c6ee1dcb5434/artifacts/apa0z5ai_WhatsApp%20Image%202026-02-13%20at%2009.38.25.jpeg',
    'disec': 'https://customer-assets.emergentagent.com/job_28206353-b4b4-425f-a884-c6ee1dcb5434/artifacts/w8uo144k_WhatsApp%20Imag%202026-02-13%20at%2009.38.28.jpeg',
    'ecosoc': 'https://customer-assets.emergentagent.com/job_28206353-b4b4-425f-a884-c6ee1dcb5434/artifacts/898c5j8o_WhatsApp%20Image%202026-0213%20at%2009.38.25.jpe.jpeg',
    'fia': 'https://customer-assets.emergentagent.com/job_28206353-b4b4-425f-a884-c6ee1dcb5434/artifacts/fn0kgxsk_WhatsApp%20Imag%202026-02-13%20at%2009.38.26.jpeg',
    'series': 'https://customer-assets.emergentagent.com/job_28206353-b4b4-425f-a884-c6ee1dcb5434/artifacts/c3o6eza9_WhatsApp%20Image%202026-0213%20at%2009.38.27.jpeg',
    'h-specpol': 'https://customer-assets.emergentagent.com/job_28206353-b4b4-425f-a884-c6ee1dcb5434/artifacts/tpx8p84m_WhatsApp%20Iage%202026-02-13%20at%2009.38.28.jpeg',
    'tbmm': 'https://customer-assets.emergentagent.com/job_28206353-b4b4-425f-a884-c6ee1dcb5434/artifacts/9x88y35m_WhatsApp%20Image%202s026-02-14%20at%2011.32.01.jpeg',
    'oligarchy': 'https://customer-assets.emergentagent.com/job_28206353-b4b4-425f-a884-c6ee1dcb5434/artifacts/q5bizkaq_WhatsApp%20Iage%202026-02-13%20at%2009.38.29.jpeg',
    'ottoman-dynasty': 'https://customer-assets.emergentagent.com/job_28206353-b4b4-425f-a884-c6ee1dcb5434/artifacts/iusi6ao3_8.jpeg'
};

// Committee Academic Team Configuration
const committeeTeamConfig = {
    'sochum': [
        { role: 'USG', roleKey: 'usg', count: 1 },
        { role: 'Chair', roleKey: 'chair', count: 1 },
        { role: 'Reporter', roleKey: 'reporter', count: 1 }
    ],
    'ecosoc': [
        { role: 'USG', roleKey: 'usg', count: 2 },
        { role: 'President Chair', roleKey: 'president-chair', count: 1 },
        { role: 'Deputy Chair', roleKey: 'deputy-chair', count: 1 }
    ],
    'fia': [
        { role: 'USG', roleKey: 'usg', count: 1 },
        { role: 'Chair', roleKey: 'chair', count: 2 }
    ],
    'series': [
        { role: 'USG', roleKey: 'usg', count: 2 },
        { role: 'Chair', roleKey: 'chair', count: 1 },
        { role: 'Reporter', roleKey: 'reporter', count: 1 }
    ],
    'h-specpol': [
        { role: 'USG', roleKey: 'usg', count: 1 },
        { role: 'Chair', roleKey: 'chair', count: 1 },
        { role: 'Reporter', roleKey: 'reporter', count: 1 }
    ],
    'tbmm': [
        { role: 'USG', roleKey: 'usg', count: 1 },
        { role: 'Chair', roleKey: 'chair', count: 1 },
        { role: 'Reporter', roleKey: 'reporter', count: 1 }
    ],
    'disec': [
        { role: 'USG', roleKey: 'usg', count: 1 },
        { role: 'Chair', roleKey: 'chair', count: 1 },
        { role: 'Reporter', roleKey: 'reporter', count: 1 }
    ],
    'oligarchy': [
        { role: 'USG', roleKey: 'usg', count: 2 },
        { role: 'Chair', roleKey: 'chair', count: 1 },
        { role: 'Reporter', roleKey: 'reporter', count: 1 }
    ],
    'ottoman-dynasty': [
        { role: 'USG', roleKey: 'usg', count: 1 },
        { role: 'Chair', roleKey: 'chair', count: 1 },
        { role: 'Reporter', roleKey: 'reporter', count: 1 }
    ]
};

// Role badge colors
const roleBadgeStyles = {
    'usg': 'bg-primary text-primary-foreground',
    'chair': 'bg-accent text-primary',
    'president-chair': 'bg-accent text-primary',
    'deputy-chair': 'bg-accent/80 text-primary',
    'reporter': 'bg-muted text-foreground'
};

// Academic Team Member Card Component
const AcademicTeamCard = ({ member, role, roleKey, index, language }) => {
    const defaultName = language === 'tr' ? 'Yakında Açıklanacak' : 'TBA';
    const initials = member?.name 
        ? member.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
        : '?';

    return (
        <div 
            className="group bg-card rounded-xl p-5 border-2 border-border hover:border-accent/50 transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
            style={{ 
                animation: `teamCardFadeIn 0.4s ease-out ${index * 0.1}s both`
            }}
        >
            <div className="flex flex-col items-center text-center">
                {/* Circular Image/Placeholder */}
                <div className="relative mb-4">
                    <div className="w-20 h-20 rounded-full overflow-hidden border-3 border-accent/30 group-hover:border-accent transition-colors duration-300 shadow-md">
                        {member?.photo ? (
                            <img 
                                src={member.photo} 
                                alt={member.name || role}
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <div className="w-full h-full bg-primary flex items-center justify-center">
                                <span className="font-display text-xl font-bold text-accent">
                                    {initials}
                                </span>
                            </div>
                        )}
                    </div>
                    {/* Gold accent ring on hover */}
                    <div className="absolute inset-0 rounded-full border-2 border-transparent group-hover:border-accent/40 transition-all duration-300 scale-110 opacity-0 group-hover:opacity-100" />
                </div>

                {/* Name */}
                <h4 className="font-display text-base font-semibold text-foreground mb-2 truncate w-full">
                    {member?.name || defaultName}
                </h4>

                {/* Role Badge */}
                <Badge 
                    className={`text-xs font-medium px-3 py-1 ${roleBadgeStyles[roleKey] || roleBadgeStyles['reporter']}`}
                >
                    {role}
                </Badge>
            </div>
        </div>
    );
};

// Academic Team Section Component
const AcademicTeamSection = ({ committeeId, teamData, language }) => {
    const config = committeeTeamConfig[committeeId];
    if (!config) return null;

    // Generate team slots based on config
    const teamSlots = [];
    config.forEach((roleConfig, roleIndex) => {
        for (let i = 0; i < roleConfig.count; i++) {
            const memberKey = `${roleConfig.roleKey}-${i}`;
            const member = teamData?.[memberKey] || null;
            const roleLabel = roleConfig.count > 1 
                ? `${roleConfig.role} ${i + 1}` 
                : roleConfig.role;
            
            teamSlots.push({
                id: `${committeeId}-${memberKey}`,
                role: roleLabel,
                roleKey: roleConfig.roleKey,
                member: member,
                index: teamSlots.length
            });
        }
    });

    return (
        <div className="border-t border-border pt-6">
            {/* Section Header */}
            <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                    <GraduationCap className="w-5 h-5 text-accent" />
                </div>
                <div>
                    <h3 className="font-display text-lg font-semibold text-foreground">
                        {language === 'tr' ? 'Akademik Kadro' : 'Academic Team'}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                        {language === 'tr' ? `${teamSlots.length} Üye` : `${teamSlots.length} Members`}
                    </p>
                </div>
            </div>

            {/* Team Grid - 3 cols desktop, 2 tablet, 1 mobile */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {teamSlots.map((slot) => (
                    <AcademicTeamCard
                        key={slot.id}
                        member={slot.member}
                        role={slot.role}
                        roleKey={slot.roleKey}
                        index={slot.index}
                        language={language}
                    />
                ))}
            </div>

            {/* Animation Styles */}
            <style>{`
                @keyframes teamCardFadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(16px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>
        </div>
    );
};

// Committee Detail Modal Component
const CommitteeDetailModal = ({ committee, isOpen, onClose, language, posterUrl }) => {
    if (!isOpen || !committee) return null;

    return (
        <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ animation: 'fadeIn 0.2s ease-out' }}
        >
            {/* Dark Overlay */}
            <div 
                className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                onClick={onClose}
                style={{ animation: 'fadeIn 0.2s ease-out' }}
            />
            
            {/* Modal Content */}
            <div 
                className="relative bg-card border border-border rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-2xl"
                style={{ animation: 'modalSlideIn 0.3s ease-out' }}
            >
                {/* Header with poster background */}
                <div className="relative h-32 bg-primary overflow-hidden">
                    {posterUrl && (
                        <img 
                            src={posterUrl} 
                            alt=""
                            className="absolute inset-0 w-full h-full object-cover opacity-30"
                        />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/80 to-transparent" />
                    
                    {/* Close Button */}
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                    
                    {/* Committee Name */}
                    <div className="absolute bottom-4 left-6 right-16">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
                                <span className="font-display font-bold text-white text-lg">
                                    {committee.name.slice(0, 2)}
                                </span>
                            </div>
                            <div>
                                <h2 className="font-display text-2xl font-bold text-white">
                                    {committee.name}
                                </h2>
                                <p className="text-accent text-sm font-medium">
                                    {committee.fullName}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Scrollable Content */}
                <div className="overflow-y-auto max-h-[calc(90vh-8rem)] p-6 space-y-6">
                    {/* Description */}
                    <div>
                        <h3 className="font-semibold text-foreground mb-2 text-sm uppercase tracking-wider">
                            {language === 'tr' ? 'Hakkında' : 'About'}
                        </h3>
                        <p className="text-muted-foreground leading-relaxed">
                            {committee.description}
                        </p>
                    </div>

                    {/* Agenda Topics */}
                    {committee.agenda && (
                        <div className="p-4 bg-accent/10 rounded-xl border border-accent/20">
                            <h3 className="font-semibold text-accent mb-2 text-sm uppercase tracking-wider flex items-center gap-2">
                                <FileText className="w-4 h-4" />
                                {language === 'tr' ? 'Gündem Konusu' : 'Agenda Topic'}
                            </h3>
                            <p className="font-medium text-foreground">{committee.agenda}</p>
                        </div>
                    )}

                    {/* Background Guide Button */}
                    {committee.backgroundGuide && (
                        <Button 
                            className="w-full btn-premium"
                            onClick={() => window.open(committee.backgroundGuide, '_blank')}
                        >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            <span>{language === 'tr' ? 'Arka Plan Kılavuzunu Görüntüle' : 'View Background Guide'}</span>
                        </Button>
                    )}

                    {/* Academic Team Section */}
                    <AcademicTeamSection
                        committeeId={committee.id}
                        teamData={committee.teamData}
                        language={language}
                    />
                </div>
            </div>

            {/* Modal Animations */}
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes modalSlideIn {
                    from { 
                        opacity: 0; 
                        transform: scale(0.95) translateY(10px); 
                    }
                    to { 
                        opacity: 1; 
                        transform: scale(1) translateY(0); 
                    }
                }
            `}</style>
        </div>
    );
};

const CommitteesPage = () => {
    const { t, language } = useLanguage();
    const [committees, setCommittees] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [selectedCommittee, setSelectedCommittee] = useState(null);

    useEffect(() => {
        const fetchCommittees = async () => {
            try {
                const data = await getCommittees();
                setCommittees(data);
            } catch (error) {
                console.error('Error fetching committees:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchCommittees();
    }, []);

    const filteredCommittees = committees.filter(committee =>
        committee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        committee.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        committee.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <>
            {/* Hero Section */}
            <section className="pt-32 pb-16 bg-primary">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl">
                        <p className="text-accent font-semibold text-sm uppercase tracking-wider mb-4">
                            {t('committees.subtitle')}
                        </p>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
                            {t('committees.title')}
                        </h1>
                        <p className="text-xl text-primary-foreground/80 leading-relaxed">
                            {t('committees.description')}
                        </p>
                    </div>
                </div>
            </section>

            {/* Search Section */}
            <section className="py-8 bg-background border-b border-border sticky top-18 z-40">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="relative w-full sm:w-96">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                            <Input
                                type="text"
                                placeholder={t('committees.searchPlaceholder')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 input-elegant"
                            />
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Users className="w-4 h-4" />
                            <span>{filteredCommittees.length} {t('committees.committeesAvailable')}</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Committees Grid */}
            <section className="py-16 bg-background">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {[...Array(6)].map((_, i) => (
                                <div key={i} className="animate-pulse">
                                    <div className="h-48 bg-muted rounded-xl" />
                                </div>
                            ))}
                        </div>
                    ) : filteredCommittees.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {filteredCommittees.map((committee, index) => {
                                const posterUrl = committeePosters[committee.id];
                                return (
                                    <div
                                        key={committee.id}
                                        onClick={() => setSelectedCommittee(committee)}
                                        className="group relative rounded-xl border border-border overflow-hidden hover:border-accent/50 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 cursor-pointer bg-card h-[280px]"
                                        style={{ animationDelay: `${index * 50}ms` }}
                                    >
                                        {/* Holographic Poster Background */}
                                        {posterUrl && (
                                            <div className="absolute inset-0 overflow-hidden">
                                                <img 
                                                    src={posterUrl} 
                                                    alt=""
                                                    className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-50 transition-all duration-500 scale-105 group-hover:scale-100"
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-card via-card/70 to-transparent group-hover:from-card/80 group-hover:via-card/50 group-hover:to-transparent transition-all duration-500" />
                                            </div>
                                        )}
                                        
                                        {/* Gold top accent */}
                                        <div className="absolute top-0 left-0 right-0 h-1 bg-accent z-10" />

                                        <div className="relative p-6 z-10 h-full flex flex-col">
                                            <div className="flex items-start justify-between mb-4">
                                                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors duration-300">
                                                    <span className="font-display font-bold text-primary text-sm">
                                                        {committee.name.slice(0, 2)}
                                                    </span>
                                                </div>
                                                <Badge 
                                                    variant="outline"
                                                    className="text-xs font-medium bg-accent/10 text-accent border-accent/20 group-hover:bg-accent/20 transition-colors duration-300"
                                                >
                                                    {language === 'tr' ? 'Detaylar için tıklayın' : 'Click for details'}
                                                </Badge>
                                            </div>

                                            <h3 className="font-display text-2xl font-bold text-foreground mb-1 group-hover:text-primary transition-colors duration-300">
                                                {committee.name}
                                            </h3>
                                            <p className="text-sm text-accent font-medium mb-4">
                                                {committee.fullName}
                                            </p>

                                            <p className="text-muted-foreground text-sm leading-relaxed line-clamp-4 flex-grow">
                                                {committee.description}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-16">
                            <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                            <h3 className="font-display text-xl font-semibold text-foreground mb-2">
                                {t('committees.noCommitteesFound')}
                            </h3>
                            <p className="text-muted-foreground">
                                {t('committees.adjustSearch')}
                            </p>
                        </div>
                    )}
                </div>
            </section>

            {/* Info Section */}
            <section className="py-16 section-cream">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-accent/10 mb-6">
                        <Sparkles className="w-8 h-8 text-accent" />
                    </div>
                    <h2 className="font-display text-3xl font-bold text-foreground mb-4">
                        {t('committees.notSure')}
                    </h2>
                    <p className="text-muted-foreground text-lg leading-relaxed">
                        {t('committees.notSureDesc')}
                    </p>
                </div>
            </section>

            {/* Committee Detail Modal */}
            <CommitteeDetailModal
                committee={selectedCommittee}
                isOpen={!!selectedCommittee}
                onClose={() => setSelectedCommittee(null)}
                language={language}
                posterUrl={selectedCommittee ? committeePosters[selectedCommittee.id] : null}
            />
        </>
    );
};

export default CommitteesPage;
